import { components, paths } from '../../generated/api/schema';
import { currentEnvAppConfig } from '../../utils';
import { storageInstance } from '../storage';
import { ApiError } from './api-error';
import { curityApiInstance } from './curity-api';
import { Credentials, isCredentialsExpired } from './transformers/credentials';
import {
  AbsenceId,
  AddressSearchRequest,
  AgreementPartnerId,
  AssignedUserDto,
  AuthenticationTypes,
  CreatePassengerRequest,
  CreateUserRequest,
  GetTripDetailsResponse,
  GetTripsPayload,
  HttpMethod,
  PassengerId,
  PassengerSearchRequest,
  PassengerSearchResponse,
  RequestInterceptor,
  ScheduleId,
  TripId,
  UpdatePassengerDetailsRequest,
  UpdatePassengerDetailsResponse,
  UpdateTripRequest,
  UpdateUserRequest,
  UserId,
  UserRole,
  UserSearchRequest,
  UserUuid,
  parseUsableUserId,
} from './types';
import type { AbsenceSearchRequest, AbsenceSearchResponse } from './types/absence-search';
import type { AgreementPartnerDto } from './types/agreement-partner';
import { isTransientRefreshError } from './types/curity';
import type {
  Request,
  RequestBody,
  RequestParams,
  RequestResponse,
} from './types/helpers';
import type {
  CreateInstitutionCostCenterAccountRequest,
  InstitutionCostCenterAccountDto,
} from './types/institution-cost-center-account';
import type {
  OperationalAttributeDto,
  PassengerDetailsDto,
  PassengerPendingReviewResponse,
  ScheduleAction,
  SchedulePendingReviewResponse,
} from './types/passenger';
import type {
  AssignPassengersRequest,
  LocationDto,
  UserDetailResponseV2,
  UserDtoV2,
  UserProfileResponseV2,
  UserSearchResponseV2,
} from './types/user-v2';
import type {
  TravelAddressRequest,
  TravelAddressResponse,
  TravelAddressSearchParams,
  TravelAddressSearchResponse,
} from './types/travel-address';

const DEFAULT_TIMEOUT_MS = 30_000;
const DEFAULT_PAGE = 0;
const DEFAULT_SIZE = 10;

const getRequestSignal = (externalSignal?: AbortSignal): AbortSignal => {
  const timeoutSignal = AbortSignal.timeout(DEFAULT_TIMEOUT_MS);
  if (!externalSignal) return timeoutSignal;

  const abortSignalWithAny = AbortSignal as typeof AbortSignal & {
    any?: (signals: AbortSignal[]) => AbortSignal;
  };
  if (typeof abortSignalWithAny.any === 'function') {
    return abortSignalWithAny.any([externalSignal, timeoutSignal]);
  }

  const controller = new AbortController();
  const abort = () => controller.abort();
  if (externalSignal.aborted || timeoutSignal.aborted) {
    abort();
  } else {
    externalSignal.addEventListener('abort', abort, { once: true });
    timeoutSignal.addEventListener('abort', abort, { once: true });
  }
  return controller.signal;
};

const paramIfDefined = (key: string, value: string | undefined): [string, string] | undefined =>
  value !== undefined ? [key, value] : undefined;

const joinedIfNonEmpty = (values: string[] | undefined): string | undefined =>
  values?.length ? values.join(',') : undefined;

const stringifyIfDefined = (value: boolean | undefined): string | undefined =>
  value !== undefined ? String(value) : undefined;

const nonDefaultValue = (value: number | undefined, defaultValue: number): string | undefined =>
  value !== undefined && value !== defaultValue ? String(value) : undefined;

const buildUserListV2SearchParams = (params: {
  query?: string;
  roles?: string[];
  // ENB-2473: confirmed via the backend's deployed OpenAPI spec (2026-09-14) that this is a
  // single `agreementId` (int64), not a comma-joined list — and that it only exists on the
  // `dev` environment so far, not yet on `test`.
  agreementId?: string;
  active?: boolean;
  page?: number;
  size?: number;
}): URLSearchParams => {
  const entries = [
    paramIfDefined('query', params.query),
    paramIfDefined('roles', joinedIfNonEmpty(params.roles)),
    paramIfDefined('agreementId', params.agreementId),
    paramIfDefined('active', stringifyIfDefined(params.active)),
    paramIfDefined('page', nonDefaultValue(params.page, DEFAULT_PAGE)),
    paramIfDefined('size', nonDefaultValue(params.size, DEFAULT_SIZE)),
  ].filter((entry): entry is [string, string] => entry !== undefined);

  return new URLSearchParams(entries);
};

class ServiceTripsApi {
  private baseURL!: string;
  private headers: Record<string, string> = {};
  private authUserId?: number;
  private authUserUuid?: string;
  private credentials?: Credentials;
  private unAuthorizedObserver?: () => void;
  private transientFailureWarningObserver?: () => void;
  private inFlightRefreshToken?: Promise<void>;
  private consecutiveTransientRefreshFailures = 0;
  private readonly TRANSIENT_FAILURE_THRESHOLD = 3;
  private lastTransientRefreshFailureAt = 0;
  private readonly TRANSIENT_FAILURE_DECAY_MS = 10 * 60 * 1000;
  // Incremented by clearSession() so an in-flight refresh started by a since-logged-out
  // session can tell it is stale, without relying on `credentials` being unset — that was
  // also (wrongly) true on a fresh page load before the first setCredentials() call (ENB-2472).
  private sessionEpoch = 0;
  private requestInterceptor?: RequestInterceptor;
  private errorInterceptor?: (e: Error) => void;

  constructor(baseURL: string) {
    if (!baseURL) throw new Error('Api base url missing');
    this.setUrl(baseURL);
  }

  setUrl = (baseURL: string) => {
    this.baseURL = baseURL;
  };

  getUri = (): string => this.baseURL;

  setCredentials(credentials: Credentials) {
    this.credentials = credentials;
    // Send raw opaque token without "bearer" prefix — matches deployed behavior.
    // The v2 API Gateway Lambda authorizer expects the raw token.
    this.headers['Authorization'] = credentials.accessToken;
  }

  // Curity does not return id_token on refresh (only issued on initial code exchange).
  // Preserve the existing id_token so identity state is never lost after a refresh.
  private mergeRefreshedCredentials(newCredentials: Credentials, oldCredentials: Credentials): Credentials {
    return newCredentials.idToken
      ? newCredentials
      : { ...newCredentials, idToken: oldCredentials.idToken };
  }

  private handleRefreshSuccess = (
    newCredentials: Credentials,
    oldCredentials: Credentials,
    epochAtStart: number,
  ) => {
    // If clearSession() ran while the refresh was in-flight, the epoch has moved on —
    // discard the result so we don't re-populate credentials or re-set the Authorization
    // header for a session that no longer exists.
    if (epochAtStart !== this.sessionEpoch) return;
    this.consecutiveTransientRefreshFailures = 0;
    const merged = this.mergeRefreshedCredentials(newCredentials, oldCredentials);
    this.setCredentials(merged);
    // Storage write failure is non-critical — in-memory creds are already valid.
    return storageInstance.setData('credentials', merged).catch(() => undefined);
  };

  private registerTransientRefreshFailure = (): void => {
    // Unrelated blips hours apart shouldn't accumulate into a logout — only a genuine
    // run of consecutive failures should.
    const now = Date.now();
    if (now - this.lastTransientRefreshFailureAt > this.TRANSIENT_FAILURE_DECAY_MS) {
      this.consecutiveTransientRefreshFailures = 0;
    }

    // Once latched at the threshold, stop stamping the clock — a sustained outage would
    // otherwise keep pushing the decay window forward and the reset could never fire.
    if (this.consecutiveTransientRefreshFailures >= this.TRANSIENT_FAILURE_THRESHOLD) return;
    this.lastTransientRefreshFailureAt = now;
    this.consecutiveTransientRefreshFailures += 1;
    if (this.consecutiveTransientRefreshFailures === this.TRANSIENT_FAILURE_THRESHOLD - 1) {
      this.transientFailureWarningObserver?.();
    }
    if (this.consecutiveTransientRefreshFailures >= this.TRANSIENT_FAILURE_THRESHOLD) {
      this.unAuthorizedObserver?.();
    }
  };

  // Track transient failures (once per Curity call, not per awaiter) and
  // force logout after TRANSIENT_FAILURE_THRESHOLD consecutive failures.
  private handleRefreshFailure = (refreshError: unknown, epochAtStart: number): never => {
    // A stale request from a session that has since logged out (clearSession() ran
    // while this was in-flight) must not touch the new session's failure counter or
    // fire unAuthorizedObserver — that would spuriously log out whatever session
    // exists now. Just propagate the error to the original caller.
    if (epochAtStart !== this.sessionEpoch) throw refreshError;
    if (isTransientRefreshError(refreshError)) {
      this.registerTransientRefreshFailure();
    } else {
      this.consecutiveTransientRefreshFailures = 0;
      this.unAuthorizedObserver?.();
    }
    throw refreshError;
  };

  refreshToken = async (oldCredentials = this.credentials): Promise<void> => {
    if (!oldCredentials) throw new Error('No credentials in storage');

    if (!this.inFlightRefreshToken) {
      const epochAtStart = this.sessionEpoch;
      this.inFlightRefreshToken = curityApiInstance
        .refreshToken(oldCredentials)
        .then((newCredentials) => this.handleRefreshSuccess(newCredentials, oldCredentials, epochAtStart))
        .catch((refreshError: unknown) => this.handleRefreshFailure(refreshError, epochAtStart))
        .finally(() => {
          // Only retire our own promise — clearSession() may already have cleared this
          // slot and a newer session may have put its own refresh there.
          if (epochAtStart === this.sessionEpoch) {
            this.inFlightRefreshToken = undefined;
          }
        });
    }
    await this.inFlightRefreshToken;
  };

  async request<Path extends keyof paths, M extends keyof Request<Path>>(
    path: Path,
    options: RequestBody<Path, M> extends never
      ? RequestParams<Path, M> extends never
        ? { method: M }
        : {
            method: M;
            params: RequestParams<Path, M>;
          }
      : RequestParams<Path, M> extends never
      ? {
          method: M;
          body: RequestBody<Path, M>;
        }
      : {
          method: M;
          body: RequestBody<Path, M>;
          params: RequestParams<Path, M>;
        }
  ) {
    if (!('Cypress' in globalThis)) {
      if (!this.credentials) {
        throw new Error('No credentials in storage');
      }
      if (isCredentialsExpired(this.credentials)) {
        await this.refreshToken();
      }
    }

    const method = String(options.method).toUpperCase();
    const body = 'body' in options ? options.body : undefined;
    const params =
      'params' in options
        ? (options.params as Record<string, unknown>)
        : undefined;
    const url = this.buildUrl(String(path), params);

    const r = async (): Promise<RequestResponse<Path, M>> => {
      const headers = Object.fromEntries(
        Object.entries({
          ...this.headers,
          ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
        }).filter(([, v]) => v !== undefined && v !== null)
      );

      const init: RequestInit = {
        method,
        headers,
        signal: AbortSignal.timeout(DEFAULT_TIMEOUT_MS),
        ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
      };

      const resolvedInit = this.requestInterceptor
        ? await this.requestInterceptor(url, init)
        : init;

      let response: Response;
      try {
        response = await fetch(url, resolvedInit);
      } catch (e) {
        this.errorInterceptor?.(e as Error);
        throw e;
      }

      if (!response.ok) {
        let body: unknown;
        try {
          const text = await response.text();
          body = text ? JSON.parse(text) : undefined;
        } catch {
          // body stays undefined if it can't be parsed
        }
        const error = new ApiError(response.status, response.statusText || `HTTP ${response.status}`, body);
        this.errorInterceptor?.(error);
        throw error;
      }

      const text = await response.text();
      const data: RequestResponse<Path, M> =
        response.status === 204 || !text
          ? (undefined as unknown as RequestResponse<Path, M>)
          : (() => {
              try {
                return JSON.parse(text) as RequestResponse<Path, M>;
              } catch {
                throw new ApiError(
                  response.status,
                  `Expected JSON response but got unparseable body from ${method} ${url}`
                );
              }
            })();

      if (typeof data === 'object' && data !== null && 'errors' in data) {
        throw data;
      }
      return data;
    };

    try {
      return await r();
    } catch (error: unknown) {
      if (error instanceof ApiError && error.status === 401) {
        try {
          await this.refreshToken();
        } catch {
          // Counter logic and unAuthorizedObserver are handled inside refreshToken().
          throw error;
        }
        try {
          return await r();
        } catch (innerError: unknown) {
          if (innerError instanceof ApiError && innerError.status === 401) {
            this.unAuthorizedObserver?.();
          }
          throw innerError;
        }
      }
      throw error;
    }
  }

  setUnAuthorizedObserver = (callback: () => void) => {
    this.unAuthorizedObserver = callback;
  };

  setTransientFailureWarningObserver = (callback: () => void) => {
    this.transientFailureWarningObserver = callback;
  };

  private buildUrl(path: string, params?: Record<string, unknown>): string {
    const url = `${this.baseURL}${path}`;
    if (!params) return url;
    const query = new URLSearchParams(
      Object.entries(params)
        .filter(([, v]) => v !== undefined)
        .map(([k, v]) => [k, String(v)])
    ).toString();
    return query ? `${url}?${query}` : url;
  }

  setInterceptors(
    requestInterceptor: RequestInterceptor,
    errorInterceptor: (e: Error) => void
  ) {
    this.requestInterceptor = requestInterceptor;
    this.errorInterceptor = errorInterceptor;
  }

  setAuthUserId = (userId: number) => {
    this.authUserId = userId;
  };

  setAuthUserUuid = (uuid: string) => {
    this.authUserUuid = uuid;
  };

  clearSession = (): void => {
    this.credentials = undefined;
    this.authUserId = undefined;
    this.authUserUuid = undefined;
    this.consecutiveTransientRefreshFailures = 0;
    this.lastTransientRefreshFailureAt = 0;
    this.sessionEpoch += 1;
    // The epoch guards the *result* of a retired refresh; this stops a new session from
    // joining the retired one's single-flight promise and inheriting its outcome.
    this.inFlightRefreshToken = undefined;
    delete this.headers['Authorization'];
  };

  getAuthUserUuid = (): string | undefined => this.authUserUuid;

  /** Fetches the logged-in user's profile (includes roles). */
  getProfile = async (): Promise<UserProfileResponseV2> => {
    return await this.request('/v2/users/profile', { method: 'get' });
  };

  getAuthPermissions = async () => {
    const data = await this.request('/v2/index', { method: 'get' });
    return data;
  };

  getUser = async (userId: UserId) => {
    const data = await this.request(`/v2/users/${userId}`, { method: 'get' });
    return data;
  };

  getUserList = async (payload: UserSearchRequest = {}) => {
    const url = `${this.baseURL}/v2/users/search`;
    const data = await this.rawPost<components['schemas']['UserSearchResponseDto']>(url, payload);
    return data;
  };

  createUser = async (payload: CreateUserRequest) => {
    const normalized = { ...payload, email: payload.email?.toLowerCase() };
    const data: components['schemas']['CreateUserResponse'] =
      await this.request('/v2/users', {
        method: 'post',
        body: normalized,
      });
    return data;
  };

  editUser = async (id: UserId, payload: UpdateUserRequest) => {
    const data: components['schemas']['UserDetailsDto'] = await this.request(
      `/v2/users/${id}`,
      {
        method: 'put',
        body: payload,
      }
    );
    return data;
  };

  // V2 endpoints (ENB-1610)
  // These use UUID paths not yet in the generated schema, so we cast to satisfy the typed request.
  getUserV2 = async (uuid: UserUuid): Promise<UserDetailResponseV2> => {
    const userId = parseUsableUserId(uuid);
    if (!userId) throw new Error(`Invalid user UUID: ${uuid}`);
    const url = `${this.baseURL}/v2/users/${userId}`;
    const data = await this.rawGet<UserDetailResponseV2>(url);
    if (!data || typeof data.permissions !== 'object') {
      throw new ApiError(502, 'V2 user response missing required permissions field');
    }
    return data;
  };

  createUserV2 = async (payload: CreateUserRequest): Promise<{ id: string }> => {
    const normalized = { ...payload, email: payload.email?.toLowerCase() };
    const url = `${this.baseURL}/v2/users`;
    return this.rawPost<{ id: string }>(url, normalized);
  };

  editUserV2 = async (uuid: UserUuid, payload: UpdateUserRequest): Promise<UserDetailResponseV2> => {
    const userId = parseUsableUserId(uuid);
    if (!userId) throw new Error(`Invalid user UUID: ${uuid}`);
    const url = `${this.baseURL}/v2/users/${userId}`;
    return this.rawPut<UserDetailResponseV2>(url, payload);
  };

  deleteUserV2 = async (uuid: UserUuid): Promise<void> => {
    const userId = parseUsableUserId(uuid);
    if (!userId) throw new Error(`Invalid user UUID: ${uuid}`);
    const url = `${this.baseURL}/v2/users/${userId}`;
    await this.rawRequest<void>(url, 'DELETE');
  };

  resendActivationV2 = async (uuid: UserUuid): Promise<void> => {
    const userId = parseUsableUserId(uuid);
    if (!userId) throw new Error(`Invalid user UUID: ${uuid}`);
    const url = `${this.baseURL}/v2/users/${userId}/resend-activation`;
    await this.rawRequest<void>(url, 'POST');
  };

  getUserListV2 = async (params: {
    query?: string;
    roles?: string[];
    agreementId?: string;
    active?: boolean;
    page?: number;
    size?: number;
  }): Promise<UserSearchResponseV2<UserDtoV2>> => {
    const qs = buildUserListV2SearchParams(params).toString();
    const url = `${this.baseURL}/v2/users/search${qs ? `?${qs}` : ''}`;
    return this.rawGet<UserSearchResponseV2<UserDtoV2>>(url);
  };

  assignPassengers = async (payload: AssignPassengersRequest): Promise<void> => {
    const url = `${this.baseURL}/v2/users/passengers/assign`;
    await this.rawPost<void>(url, payload);
  };

  getUserRolesV2 = async (): Promise<string[]> => {
    const url = `${this.baseURL}/v2/roles`;
    return this.rawGet<string[]>(url);
  };

  getAuthenticationTypesV2 = async (): Promise<string[]> => {
    const url = `${this.baseURL}/v2/users/authentication-types`;
    return this.rawGet<string[]>(url);
  };

  /**
   * Raw HTTP client for V2 endpoints not yet in the OpenAPI schema.
   * Handles auth, token refresh, and the 401 retry cycle — same as `this.request()`.
   */
  private rawRequest = async <T>(
    url: string,
    method: HttpMethod,
    body?: unknown,
    signal?: AbortSignal,
  ): Promise<T> => {
    if (!('Cypress' in globalThis)) {
      if (!this.credentials) throw new Error('No credentials in storage');
      if (isCredentialsExpired(this.credentials)) await this.refreshToken();
    }

    const doFetch = async (): Promise<T> => {
      const headers: Record<string, string> = {
        ...this.headers,
        ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      };
      const init: RequestInit = {
        method: method.toUpperCase(),
        headers,
        signal: getRequestSignal(signal),
        ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
      };
      const resolvedInit = this.requestInterceptor
        ? await this.requestInterceptor(url, init)
        : init;
      const response = await fetch(url, resolvedInit);
      if (!response.ok) {
        let errorBody: unknown;
        try {
          const text = await response.text();
          errorBody = text ? JSON.parse(text) : undefined;
        } catch {
          // body stays undefined if it can't be parsed
        }
        const error = new ApiError(response.status, response.statusText || `HTTP ${response.status}`, errorBody);
        this.errorInterceptor?.(error);
        throw error;
      }
      if (response.status === 204) return undefined as unknown as T;
      return response.json() as Promise<T>;
    };

    try {
      return await doFetch();
    } catch (error: unknown) {
      if (error instanceof ApiError && error.status === 401) {
        try {
          await this.refreshToken();
        } catch {
          // Counter logic and unAuthorizedObserver are handled inside refreshToken().
          throw error;
        }
        try {
          return await doFetch();
        } catch (innerError: unknown) {
          if (innerError instanceof ApiError && innerError.status === 401) {
            this.unAuthorizedObserver?.();
          }
          throw innerError;
        }
      }
      throw error;
    }
  };

  private rawGet = <T>(url: string, signal?: AbortSignal): Promise<T> =>
    this.rawRequest<T>(url, 'GET', undefined, signal);
  private rawPost = <T>(url: string, body: unknown): Promise<T> => this.rawRequest<T>(url, 'POST', body);
  private rawPut = <T>(url: string, body: unknown): Promise<T> => this.rawRequest<T>(url, 'PUT', body);

  private normalizePassengerDetails = (data: unknown): PassengerDetailsDto => {
    if (typeof data !== 'object' || data === null) {
      return data as PassengerDetailsDto;
    }

    const passenger = data as Record<string, unknown>;
    if (!('attributes' in passenger) && 'needs' in passenger) {
      const { needs, ...rest } = passenger;
      return {
        ...rest,
        attributes: needs,
      } as PassengerDetailsDto;
    }

    return data as PassengerDetailsDto;
  };

  getPassenger = async (passengerId: PassengerId): Promise<PassengerDetailsDto> => {
    const data = await this.request(`/v2/passengers/${passengerId}`, {
      method: 'get',
    });
    return this.normalizePassengerDetails(data);
  };

  getPassengerAddresses = async (passengerId: PassengerId) => {
    const data = await this.request(`/v2/passengers/${passengerId}/addresses`, {
      method: 'get',
    });
    return data?._embedded?.passengerAddresses ?? [];
  };

  /**
   * GET /v2/passengers/{id}/assigned-users — no query params, returns the full list.
   * HAL-wrapped: `{ _embedded: { assignedUserDtoList: [...] } }`, not a bare array
   * (ENB-2452, backend branch fix/separate-assigned-users).
   */
  getPassengerAssignedUsers = async (passengerId: PassengerId): Promise<AssignedUserDto[]> => {
    const url = `${this.baseURL}/v2/passengers/${passengerId}/assigned-users`;
    const data = await this.rawGet<{ _embedded?: { assignedUserDtoList?: AssignedUserDto[] } }>(url);
    return data?._embedded?.assignedUserDtoList ?? [];
  };

  getPassengerList = async (input: PassengerSearchRequest): Promise<PassengerSearchResponse> => {
    const data = await this.request('/v2/passengers/search', {
      method: 'post',
      body: input,
    });
    return data as PassengerSearchResponse;
  };

  createPassenger = async (payload: CreatePassengerRequest) => {
    const data = await this.request('/v2/passengers', {
      method: 'post',
      body: payload,
    });
    return data;
  };

  editPassenger = async (
    id: PassengerId,
    payload: UpdatePassengerDetailsRequest
  ): Promise<UpdatePassengerDetailsResponse> => {
    const data = await this.request(`/v2/passengers/${id}`, {
      method: 'put',
      body: payload,
    });
    return data;
  };

  getTripsList = async (
    payload: GetTripsPayload
  ) => {
    const data = await this.request('/v2/trips/search', {
      method: 'post',
      body: {
        address: payload.address,
        name: payload.name,
        passengerId: payload.personId || 0,
        sortField: payload.sortField || undefined,
        sortOrder: payload.sortOrder,
        fromDate: payload.fromDate,
        toDate: payload.toDate,
        grouping: payload.grouping,
        itemsPerPage: payload.itemsPerPage,
        index: payload.index,
        agreementIds: payload.agreementIds ? [payload.agreementIds] : undefined,
      },
    });

    return { items: data?.trips, count: data?.totalNumberOfTrips };
  };

  addressSearch = async (payload: AddressSearchRequest) => {
    const data = await this.request('/v2/addresses/search', {
      method: 'post',
      body: payload,
    });
    return data?._embedded?.addresses;
  };

  getTrip = async (tripId: TripId): Promise<GetTripDetailsResponse> =>
    this.request(`/v2/trips/${tripId}`, {
      method: 'get',
    }) as Promise<GetTripDetailsResponse>;

  getTrackingIdByB2POrderId = async (
    passengerId: number,
    b2pOrderId: string
  ): Promise<components['schemas']['TrackingIdDto']> =>
    this.request(
      `/v1/tracking/b2p/passenger/${passengerId}/trackingId/${encodeURIComponent(
        b2pOrderId
      )}` as const,
      { method: 'get' }
    );

  cancelTrip = async (
    tripId: TripId,
    reason?: string
  ) =>
    this.request(`/v2/trips/${tripId}/cancel`, {
      method: 'put',
      body: { reason },
    }).then((response) => {
      if (
        'errors' in response &&
        Array.isArray(response.errors) &&
        response.errors?.some((e) => e.code !== '200')
      ) {
        throw response.errors;
      }
      return response;
    });

  rebookTrip = async (
    tripId: TripId,
    reason?: string
  ) =>
    this.request(`/v2/trips/${tripId}/rebook`, {
      method: 'put',
      body: { reason },
    });

  updateTrip = async (
    tripId: TripId,
    payload: UpdateTripRequest
  ) => {
    const data = await this.request(`/v2/trips/${tripId}`, {
      method: 'put',
      body: payload,
    });
    return data;
  };

  sendMessage = async ({
    tripId,
    message
  }: {
    tripId: number;
    message: string;
  }) => {
    const data = await this.request(`/v2/trips/${tripId}/contact-driver`, {
      method: 'post',
      body: { message },
    });
    return data;
  };

  getAttributes = async (type?: 'passenger' | 'operational') => {
    const data = await this.request('/v2/attributes', {
      method: 'get',
      params: { type },
    });
    return data._embedded?.attributeDtoList ?? [];
  };

  getAddressTypes = async () => {
    const data = await this.request('/v2/addresses/types', { method: 'get' });
    return data._embedded?.addressTypes ?? [];
  };

  getAddressInstitutions = async () => {
    const data = await this.request('/v2/addresses/institutions', { method: 'get' });
    return data._embedded?.institutions ?? [];
  };

  getContactTypes = async () => {
    const data = await this.request('/v2/contact-types', { method: 'get' });
    return data._embedded?.contactTypes ?? [];
  };

  getAgreements = async (targetUser?: string) => {
    try {
      const params: Record<string, unknown> = {};
      if (targetUser) params['targetUser'] = targetUser;
      const data = await this.request('/v2/agreements', { method: 'get', params });
      return data?._embedded?.agreements ?? [];
    } catch (error) {
      if (error instanceof ApiError && error.status === 403) return [];
      throw error;
    }
  };

  getAgreementsByIds = async (agreementIds: number[]) => {
    const params = { agreementIds: agreementIds.join(',') } as Record<string, unknown>;
    try {
      const data = await this.request('/v2/agreements', { method: 'get', params });
      return data?._embedded?.agreements ?? [];
    } catch (error) {
      if (error instanceof ApiError && error.status === 403) return [];
      throw error;
    }
  };

  getUserRoles = async (): Promise<UserRole[]> => {
    const url = `${this.baseURL}/v2/users/roles`;
    return this.rawGet<UserRole[]>(url);
  };

  getRoles = async (): Promise<UserRole[]> => {
    const url = `${this.baseURL}/v2/roles`;
    return this.rawGet<UserRole[]>(url);
  };

  getAuthenticationTypes = async () => {
    const url = `${this.baseURL}/v2/users/authentication-types`;
    const data = await this.rawGet<{ _embedded?: { authenticationTypes?: AuthenticationTypes } }>(url);
    return data?._embedded?.authenticationTypes;
  };

  getUserNameAvailability = async (userName: string) => {
    const data = await this.request(`/v2/users/${userName.toLowerCase()}/availability`, {
      method: 'get',
    });
    return data;
  };

  getAgreementPartners = async (): Promise<AgreementPartnerDto[]> => {
    const url = `${this.baseURL}/v2/agreement-partners`;
    const data = await this.rawGet<{ _embedded?: { agreementPartners?: AgreementPartnerDto[] } }>(url);
    return data?._embedded?.agreementPartners ?? [];
  };

  createAgreementPartner = async (payload: { name: string }): Promise<AgreementPartnerDto | undefined> => {
    const url = `${this.baseURL}/v2/agreement-partners`;
    return this.rawPost<AgreementPartnerDto | undefined>(url, payload);
  };

  createInstitutionCostCenterAccount = async (
    payload: CreateInstitutionCostCenterAccountRequest,
  ): Promise<InstitutionCostCenterAccountDto> => {
    const url = `${this.baseURL}/v2/institution-cost-center-accounts`;
    return this.rawPost<InstitutionCostCenterAccountDto>(url, payload);
  };

  getTravelAddresses = async (
    params: TravelAddressSearchParams = {},
    signal?: AbortSignal,
  ): Promise<TravelAddressSearchResponse> => {
    const url = this.buildUrl('/v2/travel-addresses', {
      query: params.query,
      page: params.page,
      size: params.size,
    });
    return this.rawGet<TravelAddressSearchResponse>(url, signal);
  };

  createTravelAddress = async (
    payload: TravelAddressRequest,
  ): Promise<TravelAddressResponse> =>
    this.rawPost<TravelAddressResponse>(
      `${this.baseURL}/v2/travel-addresses`,
      payload,
    );

  updateTravelAddress = async (
    id: number,
    payload: TravelAddressRequest,
  ): Promise<TravelAddressResponse> =>
    this.rawPut<TravelAddressResponse>(
      `${this.baseURL}/v2/travel-addresses/${id}`,
      payload,
    );

  updateAgreementPartner = async (id: AgreementPartnerId, payload: { name: string }): Promise<AgreementPartnerDto | undefined> => {
    const url = `${this.baseURL}/v2/agreement-partners/${id}`;
    return this.rawPut<AgreementPartnerDto | undefined>(url, payload);
  };

  deleteAgreementPartner = async (id: AgreementPartnerId): Promise<void> => {
    const url = `${this.baseURL}/v2/agreement-partners/${id}`;
    await this.rawRequest<void>(url, 'DELETE');
  };

  /** GET /v2/addresses/travel-points — global list of travel point locations. */
  getLocations = async (): Promise<LocationDto[]> => {
    const url = `${this.baseURL}/v2/addresses/travel-points`;
    const data = await this.rawGet<components['schemas']['TravelPointDto'][]>(url);

    return data.flatMap(({ travelPointId, institutionName, address }) =>
      typeof travelPointId === 'number'
        ? [{
            id: travelPointId,
            institutionName:
              typeof institutionName === 'string' ? institutionName : '',
            address: typeof address === 'string' ? address : '',
          }]
        : [],
    );
  };

  getOperationalAttributes = async (): Promise<OperationalAttributeDto[]> => {
    const data = await this.request('/v2/attributes', { method: 'get', params: { type: 'operational' } });
    return data._embedded?.attributeDtoList ?? [];
  };

  approvePassenger = async (passengerId: PassengerId): Promise<components['schemas']['PassengerDetailsDto']> => {
    return this.request(`/v2/passengers/${passengerId}/approve`, { method: 'put' });
  };

  rejectPassenger = async (passengerId: PassengerId): Promise<components['schemas']['PassengerDetailsDto']> => {
    return this.request(`/v2/passengers/${passengerId}/reject`, { method: 'put' });
  };

  /** GET /v2/passengers/{passengerId}/pending-review — current values alongside pending changes awaiting approval */
  getPassengerPendingReview = async (passengerId: PassengerId): Promise<PassengerPendingReviewResponse> => {
    return this.request(`/v2/passengers/${passengerId}/pending-review`, { method: 'get' });
  };

  getPendingReview = async (): Promise<components['schemas']['PendingReviewPassengerDto'][]> => {
    try {
      const data = await this.request('/v2/passengers/pending-review', { method: 'get' });
      return data ?? [];
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) return [];
      throw e;
    }
  };

  activatePassenger = async (passengerId: PassengerId): Promise<components['schemas']['PassengerDetailsDto']> => {
    return this.request(`/v2/passengers/${passengerId}/activate`, { method: 'post' });
  };

  completePassenger = async (passengerId: PassengerId): Promise<components['schemas']['PassengerDetailsDto']> => {
    return this.request(`/v2/passengers/${passengerId}/complete`, { method: 'put' });
  };

  createAbsence = async (payload: components['schemas']['AbsenceRequest']): Promise<components['schemas']['AbsenceResponse']> => {
    return this.request('/v2/general-absence', { method: 'post', body: payload });
  };

  getScheduleTypes = async (): Promise<components['schemas']['CollectionModelScheduleTypeDto']> => {
    return this.request('/v2/schedule-types', { method: 'get' });
  };

  updateAbsence = async (absenceId: AbsenceId, payload: components['schemas']['AbsenceRequest']): Promise<components['schemas']['AbsenceResponse']> => {
    return this.request(`/v2/general-absence/${absenceId}`, { method: 'put', body: payload });
  };

  cancelAbsence = async (absenceId: AbsenceId): Promise<void> => {
    try {
      await this.request(`/v2/general-absence/${absenceId}`, { method: 'delete' });
    } catch (error: unknown) {
      // TODO(DICH-3274): The v2 API returns plain text "Deleted" on success (not JSON),
      // which causes request() to throw a parse error. If the status was 200, the
      // deletion succeeded — swallow the parse error. Remove this workaround once the
      // backend returns 204 No Content or a JSON response.
      if (error instanceof ApiError && error.status === 200) return;
      throw error;
    }
  };

  // TODO: Remove @ts-expect-error once /v2/general-absence/search is in the OpenAPI schema
  // and types are regenerated. PR #116 adds this endpoint but the schema isn't updated yet.
  searchAbsences = async (payload: AbsenceSearchRequest): Promise<AbsenceSearchResponse> => {
    // @ts-expect-error — endpoint not yet in generated OpenAPI types
    return this.request('/v2/general-absence/search', { method: 'post', body: payload });
  };

  /** GET /v2/passengers/schedules/{scheduleId}/pending-review — fetch pending changes for an edited schedule */
  getSchedulePendingReview = async (scheduleId: ScheduleId): Promise<SchedulePendingReviewResponse> => {
    return this.request(`/v2/passengers/schedules/${scheduleId}/pending-review`, { method: 'get' });
  };

  /** PUT /v2/passengers/schedules/{scheduleId}/{action} — approve or reject schedule changes */
  scheduleAction = async (scheduleId: ScheduleId, action: ScheduleAction): Promise<void> => {
    await this.request(`/v2/passengers/schedules/${scheduleId}/${action}`, { method: 'put' });
  };
}

export { ServiceTripsApi };

const { baseURL } = currentEnvAppConfig;
export const apiInstance = new ServiceTripsApi(baseURL);
